<script setup>
// ShareNotePopover.vue — v5
// - Abre sem link: mostra botão "Gerar link"
// - Botão revogar só aparece quando há link ativo (shareUrl preenchido) E isOwner

import { watch } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  noteTitle: { type: String, default: "" },
  shareUrl: { type: String, default: "" },
  isGenerating: { type: Boolean, default: false },
  isCopied: { type: Boolean, default: false },
  isRevoking: { type: Boolean, default: false },
  shareError: { type: String, default: "" },
  noteId: { type: Number, default: null },
  isOwner: { type: Boolean, default: false },
});

const emit = defineEmits([
  "close",
  "generate", // >>> NOVO: disparado pelo botão "Gerar link"
  "copy",
  "share-whatsapp",
  "share-email",
  "share-native",
  "revoke",
]);

const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

// 🔹 NOVO: arrastar o diálogo pelo cabeçalho. Nem todo uso desmonta o
// componente entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="share-overlay" @click.self="emit('close')">
      <div
        ref="panelRef"
        class="share-popover"
        role="dialog"
        aria-label="Compartilhar nota"
        :style="panelStyle"
      >
        <!-- Cabeçalho -->
        <div
          class="share-header"
          :class="{
 'share-header--draggable': isDraggable,
            'share-header--dragging': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h3 class="share-title">
            <span class="share-title-icon-chip">
              <i class="fas fa-share-alt"></i>
            </span>
            Compartilhar nota
          </h3>
          <button
            type="button"
            class="share-close-btn"
            aria-label="Fechar"
            @click="emit('close')"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <!-- Gerando -->
        <div v-if="isGenerating" class="share-loading">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Gerando link…</span>
        </div>

        <!-- Erro -->
        <div v-else-if="shareError" class="share-error">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ shareError }}</span>
          <!-- Permite tentar gerar novamente após erro -->
          <button
            type="button"
            class="share-btn share-btn-generate share-btn-retry"
            @click="emit('generate', noteId)"
          >
            <i class="fas fa-redo"></i>
            <span>Tentar novamente</span>
          </button>
        </div>

        <!-- >>> NOVO: sem link ainda — mostra botão "Gerar link" -->
        <div v-else-if="!shareUrl" class="share-body share-body-empty">
          <p class="share-notice">
            <i class="fas fa-link share-notice-icon"></i>
            Esta nota ainda não tem um link de compartilhamento.
          </p>
          <button
            type="button"
            class="share-btn share-btn-generate"
            @click="
              () => {
                console.log('[popover] clique gerar, noteId:', noteId);
                emit('generate', noteId);
              }
            "
          >
            <i class="fas fa-link"></i>
            <span>Gerar link</span>
          </button>
        </div>

        <!-- Link ativo — conteúdo principal -->
        <div v-else class="share-body">
          <div class="share-link-row">
            <input
              type="text"
              class="share-link-input"
              :value="shareUrl"
              readonly
              aria-label="Link de compartilhamento"
            />
            <button
              type="button"
              class="share-btn share-btn-copy"
              :class="{ 'share-btn-copied': isCopied }"
              @click="emit('copy')"
            >
              <i :class="isCopied ? 'fas fa-check' : 'fas fa-copy'"></i>
              <span>{{ isCopied ? "Copiado!" : "Copiar" }}</span>
            </button>
          </div>

          <div class="share-actions">
            <button
              type="button"
              class="share-btn share-btn-whatsapp"
              @click="emit('share-whatsapp')"
            >
              <i class="fab fa-whatsapp"></i>
              <span>WhatsApp</span>
            </button>
            <button
              type="button"
              class="share-btn share-btn-email"
              @click="emit('share-email')"
            >
              <i class="fas fa-envelope"></i>
              <span>E-mail</span>
            </button>
            <button
              v-if="hasNativeShare"
              type="button"
              class="share-btn share-btn-native"
              @click="emit('share-native')"
            >
              <i class="fas fa-share"></i>
              <span>Compartilhar</span>
            </button>
          </div>

          <hr class="share-separator" />

          <p class="share-notice">
            <i class="fas fa-globe share-notice-icon"></i>
            Qualquer pessoa com este link pode ver a nota, mesmo sem conta no
            Litterae.
          </p>

          <!-- Revogar: só aparece quando há link ativo (shareUrl) E é o dono -->
          <div v-if="isOwner && shareUrl" class="share-revoke-row">
            <button
              type="button"
              class="share-btn share-btn-revoke"
              :disabled="isRevoking"
              @click="emit('revoke', noteId)"
            >
              <i
                :class="
 isRevoking ? 'fas fa-spinner fa-spin' : 'fas fa-link-slash'
                "
              ></i>
              <span>{{ isRevoking ? "Revogando…" : "Revogar link" }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.share-overlay {
  position: fixed;
  inset: 0;
  background: var(--backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  z-index: 1100;
}

.share-popover {
  background: var(--bg-content);
  border-radius: 16px;
  width: 100%;
  max-width: 32rem;
  box-shadow: var(--shadow-modal);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2.5rem);
}

.share-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

/* 🔹 NOVO: cabeçalho como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.share-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.share-header--dragging {
  cursor: grabbing;
}
/* O botão de fechar continua sendo clique, não alça */
.share-header--draggable .share-close-btn {
  cursor: pointer;
}

.share-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.share-title-icon-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background-color: var(--success-bg);
  color: var(--success-text);
  font-size: 0.78rem;
  flex-shrink: 0;
  border: 1px solid #a7f3d0;
}

.share-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.35rem;
  border-radius: 10px;
  color: var(--text-subtle);
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.share-close-btn:hover {
  background-color: var(--neutral-200);
  color: var(--text-dark);
}

.share-loading,
.share-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 1.25rem 1rem;
  font-size: 0.88rem;
  color: var(--text-muted);
  flex-wrap: wrap;
}

.share-error {
  color: var(--danger-hover);
}

.share-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* >>> NOVO: estado sem link */
.share-body-empty {
  align-items: flex-start;
}

.share-link-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.share-link-input {
  flex: 1;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.78rem;
  color: var(--text-dark);
  background: var(--surface-2);
  outline: none;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
}

.share-btn-copy {
  background: var(--brand-soft);
  color: var(--text-on-brand-soft);
  flex-shrink: 0;
}
.share-btn-copy:hover {
  background: #ddd6fe;
}
.share-btn-copied {
  background: var(--success-bg);
  color: var(--success-text);
}

/* >>> NOVO: botão gerar link */
.share-btn-generate {
  background: var(--success-bg);
  color: var(--success-text);
  border: 1px solid #a7f3d0;
}
.share-btn-generate:hover {
  background: var(--success-bg);
}

.share-btn-retry {
  margin-left: auto;
}

.share-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.share-btn-whatsapp {
  background: var(--success-bg);
  color: #15803d;
  flex: 1;
  justify-content: center;
}
.share-btn-whatsapp:hover {
  background: #bbf7d0;
}

.share-btn-email {
  background: var(--info-border);
  color: var(--link-hover);
  flex: 1;
  justify-content: center;
}
.share-btn-email:hover {
  background: #bfdbfe;
}

.share-btn-native {
  background: var(--neutral-100);
  color: var(--text-dark);
  flex: 1;
  justify-content: center;
}
.share-btn-native:hover {
  background: var(--border-color);
}

.share-separator {
  border: none;
  border-top: 1px dashed var(--border-color);
  margin: 2px 0;
}

.share-notice {
  font-size: 0.76rem;
  color: var(--text-muted);
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.4;
  margin: 0;
}

.share-notice-icon {
  color: var(--text-subtle);
  margin-top: 1px;
  flex-shrink: 0;
}

.share-revoke-row {
  display: flex;
  justify-content: flex-end;
}

.share-btn-revoke {
  background: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
}
.share-btn-revoke:hover:not(:disabled) {
  background: var(--danger-bg);
}
.share-btn-revoke:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>